import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addToCartSuccess } from '../../store/slices/cartSlice';
import { addToWishlistSuccess } from '../../store/slices/wishlistSlice';
import { addNotification, openAuthModal } from '../../store/slices/uiSlice';
import { Product } from '../../types/product.types';
import { addRecentView } from '../../store/slices/recentViewsSlice';
import { RootState } from '../../store';
import { dbProducts } from '../../data/db';
import './ProductDetail.css';

const PRODUCT_IMAGES = [
  '/assets/images/camera.png',
  '/assets/images/camera.png',
  '/assets/images/camera.png',
  '/assets/images/camera.png',
  '/assets/images/camera.png',
];

const BULK_TIERS = [
  { label: 'Qty 2', price: '75% OFF' },
  { label: 'Qty 3', price: '75% OFF' },
  { label: 'Qty 4-5', price: '75% OFF' },
  { label: 'Qty 6-7', price: '75% OFF' },
  { label: 'Qty 8+', price: '75% OFF' },
];

const SIMILAR_PRODUCTS = [
  { id: 'prod_5', badge: 'BEST DEAL', name: 'CP PLUS Motion Sensor - Professional Series 5', rating: 4.7, reviews: 219, price: '₹1,587', img: 'https://cdn.moglix.com/p/WXHBytqNtGe5B-xxlarge.jpg' },
  { id: 'prod_4', badge: 'DEAL OF THE DAY', name: 'Hikvision Smart Door Lock - Professional Series 4', rating: 4.5, reviews: 42, price: '₹3,505', img: 'https://cdn.moglix.com/p/ZROQhxfDpItzs-xxlarge.jpg' },
  { id: 'prod_6', badge: 'DEAL OF THE DAY', name: 'Hikvision Smart Door Lock - Professional Series 6', rating: 5.0, reviews: 219, price: '₹1,013', img: 'https://cdn.moglix.com/p/GFK05uvAFEFly-xxlarge.png' },
  { id: 'prod_8', badge: 'BEST DEAL', name: 'CP PLUS Smart Door Lock - Professional Series 8', rating: 4.7, reviews: 474, price: '₹794', img: 'https://cdn.moglix.com/p/Hma8qSmh5SyqJ-xxlarge.jpg' },
];

const KEY_FEATURES = [
  'This product operates exclusively through a Wi-Fi connection and does not support 4G Sim.',
  'Keep a watchful eye on your surroundings with our high resolution wireless setup.',
  'Capture crystal-clear B2B industrial footage & stream it live with innovative smart technology.',
  'Upgrade your workplace and home security with our advanced professional catalog series.',
];

const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 14 }) => (
  <div className="pd-stars">
    {[1, 2, 3, 4, 5].map((s) => (
      <svg key={s} width={size} height={size} viewBox="0 0 24 24"
        fill={s <= rating ? '#F59E0B' : '#E5E7EB'}
        stroke={s <= rating ? '#F59E0B' : '#E5E7EB'} strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState<'features' | 'additional' | 'specification'>('features');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Load product from Redux state (populated from backend API)
  const reduxProducts = useSelector((state: RootState) => state.products?.products || []);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const getProduct = (): Product => {
    const decodedId = decodeURIComponent(id || '');
    // 1. Try Redux store first (populated from backend)
    const found = reduxProducts.find((p: any) => p.id === id || p.id === decodedId);
    if (found) {
      return {
        id: found.id,
        name: found.name,
        description: found.description || `${found.name} designed for industrial use. Features robust construction and reliable performance.`,
        sku: found.sku || found.id,
        category: found.category,
        subcategory: found.subcategory || 'Industrial',
        brand: found.brand || 'Generic',
        price: found.oldPrice || found.price,
        discountedPrice: found.price,
        currency: 'INR',
        images: [{ id: 'img-1', url: found.image || PRODUCT_IMAGES[0], alt: found.name, isMain: true }],
        specifications: [],
        inStock: found.inStock !== false,
        stockQuantity: found.stockQuantity || 10,
        minOrderQuantity: 1,
        rating: found.rating || 4.5,
        reviewCount: found.reviewsCount || 12,
        tags: found.tags || [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }

    // 2. Try static dbProducts catalog dataset
    const foundDb = dbProducts.products.find((p: any) => p.id === id || p.id === decodedId);
    if (foundDb) {
      const pPrice = parseFloat(String(foundDb.price || '0').replace(/[^0-9.]/g, '')) || 1999;
      const pOldPrice = parseFloat(String(foundDb.oldPrice || '0').replace(/[^0-9.]/g, '')) || Math.round(pPrice * 1.3);
      return {
        id: foundDb.id,
        name: foundDb.name,
        description: foundDb.description || `${foundDb.name} designed for professional B2B solutions. Features robust construction and high performance.`,
        sku: foundDb.id,
        category: foundDb.category,
        subcategory: 'Industrial',
        brand: foundDb.brand || 'Generic',
        price: pOldPrice,
        discountedPrice: pPrice,
        currency: 'INR',
        images: [{ id: 'img-1', url: foundDb.image || PRODUCT_IMAGES[0], alt: foundDb.name, isMain: true }],
        specifications: [],
        inStock: foundDb.inStock !== false,
        stockQuantity: foundDb.stockQuantity || 50,
        minOrderQuantity: 1,
        rating: parseFloat(foundDb.rating || '4.0'),
        reviewCount: foundDb.reviewsCount || 25,
        tags: [foundDb.category, foundDb.brand].filter(Boolean),
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }

    // 3. Fallback product
    return {
      id: 'prod-1',
      name: 'CP-UNC-DA21L3C-Q 2MP Network IR Dome Camera',
      description: '2MP Network IR Dome Camera - 30Mtr designed for industrial use.',
      sku: 'CP-UNC-DA21L3C-Q',
      category: 'Electronics',
      subcategory: 'Cameras',
      brand: 'CP PLUS',
      price: 2680,
      discountedPrice: 1855,
      currency: 'INR',
      images: [{ id: 'img-1', url: '/assets/images/camera.png', alt: 'Camera', isMain: true }],
      specifications: [],
      inStock: true,
      stockQuantity: 100,
      minOrderQuantity: 1,
      rating: 4.7,
      reviewCount: 79811,
      tags: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  };

  const mockProduct = getProduct();

  // Dynamically resolve product image collection
  const productImages = mockProduct.images && mockProduct.images.length > 0
    ? mockProduct.images.map(img => img.url)
    : PRODUCT_IMAGES;

  useEffect(() => {
    // Reset scroll position to top instantly when viewing a new product detail page
    window.scrollTo(0, 0);
    dispatch(addRecentView({
      id: mockProduct.id,
      name: mockProduct.name,
      brand: mockProduct.brand,
      image: productImages[0],
      rating: mockProduct.rating,
      reviewsCount: mockProduct.reviewCount,
      price: `₹${mockProduct.discountedPrice?.toLocaleString() || mockProduct.price.toLocaleString()}`,
      oldPrice: mockProduct.discountedPrice ? `₹${mockProduct.price.toLocaleString()}` : undefined,
      discount: mockProduct.discountedPrice ? `${Math.round(((mockProduct.price - mockProduct.discountedPrice) / mockProduct.price) * 100)}% OFF` : undefined,
    }));
  }, [dispatch, mockProduct.id, mockProduct.brand, mockProduct.discountedPrice, mockProduct.name, mockProduct.price, mockProduct.rating, mockProduct.reviewCount, productImages]);

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      dispatch(openAuthModal({ message: 'Log in to add items to your cart', pendingAction: 'cart' }));
      return;
    }
    dispatch(addToCartSuccess({
      id: `cart_${Date.now()}_${mockProduct.id}`,
      productId: mockProduct.id,
      product: {
        id: mockProduct.id,
        name: mockProduct.name,
        sku: mockProduct.sku,
        price: mockProduct.price,
        discountedPrice: mockProduct.discountedPrice,
        currency: mockProduct.currency,
        image: productImages[0],
        inStock: mockProduct.inStock,
        stockQuantity: mockProduct.stockQuantity
      },
      quantity: qty,
      addedAt: new Date().toISOString()
    }));
    dispatch(addNotification({ type: 'success', title: 'Added to Cart', message: `"${mockProduct.name}" added to cart successfully!` }));
  };

  const handleAddToWishlist = () => {
    if (!isAuthenticated) {
      dispatch(openAuthModal({ message: 'Log in to save items to your wishlist', pendingAction: 'wishlist' }));
      return;
    }
    dispatch(addToWishlistSuccess(mockProduct));
    dispatch(addNotification({ type: 'success', title: 'Added to Shortlist', message: `"${mockProduct.name}" added to your shortlist successfully!` }));
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      navigate('/auth/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }
    dispatch(addToCartSuccess({
      id: `cart_${Date.now()}_${mockProduct.id}`,
      productId: mockProduct.id,
      product: {
        id: mockProduct.id,
        name: mockProduct.name,
        sku: mockProduct.sku,
        price: mockProduct.price,
        discountedPrice: mockProduct.discountedPrice,
        currency: mockProduct.currency,
        image: productImages[0],
        inStock: mockProduct.inStock,
        stockQuantity: mockProduct.stockQuantity
      },
      quantity: qty,
      addedAt: new Date().toISOString()
    }));
    navigate('/checkout');
  };

  const changeQty = (delta: number) => setQty(q => Math.max(1, q + delta));

  return (
    <div className="pd-page">
      <div className="pd-container">

        {/* Breadcrumb */}
        <nav className="pd-breadcrumb">
          <Link to="/">Home</Link><span>&gt;</span>
          <Link to="/shop">Shop</Link><span>&gt;</span>
          <Link to="/shop">Shop Grid</Link><span>&gt;</span>
          <span>{mockProduct.category}</span><span>&gt;</span>
          <span className="pd-breadcrumb__current">{mockProduct.sku}</span>
        </nav>

        {/* ── Main product layout ── */}
        <div className="pd-layout">

          {/* Left — Gallery */}
          <div className="pd-gallery">
            <div className="pd-gallery__main">
              <img src={productImages[selectedImage]} alt={mockProduct.name} className="pd-gallery__main-img" />
            </div>
            <div className="pd-gallery__thumbs">
              <button className="pd-gallery__arrow pd-gallery__arrow--left">&#8249;</button>
              {productImages.map((img, i) => (
                <button
                  key={i}
                  className={`pd-gallery__thumb ${selectedImage === i ? 'pd-gallery__thumb--active' : ''}`}
                  onClick={() => setSelectedImage(i)}
                >
                  <img src={img} alt={`thumb-${i}`} />
                </button>
              ))}
              <button className="pd-gallery__arrow pd-gallery__arrow--right">&#8250;</button>
            </div>
          </div>

          {/* Right — Info */}
          <div className="pd-info">

            {/* Brand and SKU */}
            <span style={{ fontSize: '12px', textTransform: 'uppercase', color: '#df2027', fontWeight: '800', letterSpacing: '1px' }}>{mockProduct.brand}</span>

            {/* Title */}
            <h1 className="pd-info__title">{mockProduct.name}</h1>
            <p className="pd-info__subtitle">{mockProduct.description}</p>

            {/* Star ratings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <StarRating rating={mockProduct.rating} />
              <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>({mockProduct.reviewCount.toLocaleString()} reviews)</span>
            </div>

            {/* Availability */}
            <div className="pd-info__availability">
              <span className="pd-info__avail-label">{mockProduct.stockQuantity} items in stock</span>
              <span className="pd-info__avail-sep">·</span>
              <span className="pd-info__avail-text">Availability:</span>
              <span className="pd-info__avail-badge" style={{ background: mockProduct.inStock ? '#10b981' : '#ef4444', color: '#fff' }}>
                {mockProduct.inStock ? 'In Stock' : 'Out of Stock'}
              </span>
            </div>

            {/* Price */}
            <div className="pd-info__price-row">
              <span className="pd-info__price">₹{(mockProduct.discountedPrice || mockProduct.price).toLocaleString('en-IN')}</span>
              {mockProduct.discountedPrice && mockProduct.discountedPrice < mockProduct.price && (
                <>
                  <span className="pd-info__price-orig">₹{mockProduct.price.toLocaleString('en-IN')}</span>
                  <span className="pd-info__off-badge">
                    {Math.round(((mockProduct.price - mockProduct.discountedPrice) / mockProduct.price) * 100)}% OFF
                  </span>
                </>
              )}
            </div>

            {/* Bulk pricing */}
            <div className="pd-bulk">
              <div className="pd-bulk__header">Buy More &amp; Save More (B2B Bulk Pricing Available)</div>
              <div className="pd-bulk__tiers">
                {BULK_TIERS.map((t) => (
                  <div key={t.label} className="pd-bulk__tier">
                    <div className="pd-bulk__qty">{t.label}</div>
                    <div className="pd-bulk__price">₹{Math.round((mockProduct.discountedPrice || mockProduct.price) * 0.95).toLocaleString('en-IN')} /pc</div>
                    <div className="pd-bulk__off">{t.price}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pd-actions">
              <div className="pd-actions__qty">
                <button onClick={() => changeQty(-1)}>−</button>
                <span>{qty}</span>
                <button onClick={() => changeQty(1)}>+</button>
              </div>
              <button className="pd-actions__cart" onClick={handleAddToCart} disabled={!mockProduct.inStock} style={{ opacity: mockProduct.inStock ? 1 : 0.6 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                ADD TO CART
              </button>
              <button className="pd-actions__buy" onClick={handleBuyNow} disabled={!mockProduct.inStock} style={{ opacity: mockProduct.inStock ? 1 : 0.6 }}>BUY NOW</button>
              <button className="pd-actions__cart" onClick={handleAddToWishlist} style={{ backgroundColor: 'transparent', color: '#333', border: '1px solid #ccc', marginLeft: '10px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                WISHLIST
              </button>
            </div>

            {/* B2B badge */}
            <div className="pd-b2b">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ABCE8" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>India's Leading B2B Online Store</span>
              <span className="pd-b2b__sub">Trusted by 2.5+ lac 10+ years</span>
            </div>

            {/* Trust badges */}
            <div className="pd-trust">
              <div className="pd-trust__item">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="1.8">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
                <div><div className="pd-trust__title">4+1</div><div className="pd-trust__sub">Day Return</div></div>
              </div>
              <div className="pd-trust__item">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="1.8">
                  <rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
                <div><div className="pd-trust__title">All India</div><div className="pd-trust__sub">Delivery</div></div>
              </div>
              <div className="pd-trust__item">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="1.8">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.37a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <div><div className="pd-trust__title">24/7</div><div className="pd-trust__sub">Customer Support</div></div>
              </div>
              <div className="pd-trust__item">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="1.8">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <div><div className="pd-trust__title">100%</div><div className="pd-trust__sub">Secure Payment</div></div>
              </div>
            </div>

          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="pd-tabs">
          <div className="pd-tabs__nav">
            {(['features', 'additional', 'specification'] as const).map((tab) => (
              <button
                key={tab}
                className={`pd-tabs__btn ${activeTab === tab ? 'pd-tabs__btn--active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'features' ? 'KEY FEATURES' : tab === 'additional' ? 'ADDITIONAL INFORMATION' : 'SPECIFICATION'}
              </button>
            ))}
          </div>
          <div className="pd-tabs__content">
            {activeTab === 'features' && (
              <div>
                <h4 className="pd-tabs__section-title">Key Features</h4>
                <ul className="pd-tabs__feature-list">
                  {KEY_FEATURES.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {activeTab === 'additional' && <p>Additional wholesale specifications and compliance documents are available upon raising an RFQ query.</p>}
            {activeTab === 'specification' && (
              <table className="pd-specs-table">
                <tbody>
                  <tr><td>Brand</td><td>{mockProduct.brand}</td></tr>
                  <tr><td>SKU</td><td>{mockProduct.sku}</td></tr>
                  <tr><td>Category</td><td>{mockProduct.category}</td></tr>
                  <tr><td>Availability</td><td>{mockProduct.inStock ? 'In Stock' : 'Out of Stock'}</td></tr>
                  <tr><td>Minimum Bulk Quantity</td><td>{mockProduct.minOrderQuantity} Unit</td></tr>
                  <tr><td>Warranty</td><td>1 Year B2B Manufacturer Warranty</td></tr>
                </tbody>
              </table>
            )}

          </div>
        </div>

        {/* ── Similar Products ── */}
        <div className="pd-similar">
          <h2 className="pd-similar__title">Similar Products</h2>
          <div className="pd-similar__wrap">
            <button className="pd-similar__arrow pd-similar__arrow--left">&#8249;</button>
            <div className="pd-similar__track">
              {SIMILAR_PRODUCTS.map((p) => (
                <Link to={`/product/${p.id}`} key={p.id} className="pd-similar__card">
                  <div className={`pd-similar__badge pd-similar__badge--${p.badge.includes('BEST') ? 'best' : 'deal'}`}>{p.badge}</div>
                  <div className="pd-similar__img-wrap">
                    <img src={p.img} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </div>
                  <div className="pd-similar__info">
                    <p className="pd-similar__name" style={{ height: '36px', overflow: 'hidden' }}>{p.name}</p>
                    <span className="pd-similar__price">From <strong>{p.price}</strong></span>
                  </div>
                </Link>
              ))}
            </div>
            <button className="pd-similar__arrow pd-similar__arrow--right">&#8250;</button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;
